import { cn } from "@/lib/cn"

export type FeedWorkflowStep = {
  id: number | null
  key: string
  label: string
  position: number
  state: string
  error: string | null
  metadata: Record<string, unknown>
  started_at: string | null
  completed_at: string | null
}

export type FeedWorkflowPayload = {
  feed_run_id: number
  feed_id: number | null
  feed_type: string | null
  run_state: string | null
  steps: FeedWorkflowStep[]
}

const STATE_STYLES: Record<string, string> = {
  pending: "border-zinc-200 bg-zinc-50 text-zinc-600",
  running: "border-sky-300 bg-sky-50 text-sky-800",
  completed: "border-emerald-300 bg-emerald-50 text-emerald-800",
  failed: "border-rose-300 bg-rose-50 text-rose-800",
  skipped: "border-zinc-200 bg-white text-zinc-400",
}

function stateLabel(state: string): string {
  return state.replaceAll("_", " ")
}

export function FeedRunWorkflow({ payload }: { payload: FeedWorkflowPayload }) {
  if (!payload.steps.length) return null

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6" aria-label="Feed run workflow">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Workflow</h2>
        <p className="text-sm text-zinc-500">Linear pipeline for this run</p>
      </div>
      <ol className="space-y-3">
        {payload.steps.map((step, index) => (
          <li
            key={`${step.key}-${step.position}`}
            className={cn("rounded-lg border px-4 py-3", STATE_STYLES[step.state] ?? STATE_STYLES.pending)}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide opacity-70">
                  Step {index + 1}
                </p>
                <p className="text-base font-semibold">{step.label}</p>
                {typeof step.metadata?.summary === "string" && (
                  <p className="mt-1 text-sm opacity-80">{step.metadata.summary}</p>
                )}
                {step.error && (
                  <pre className="mt-2 whitespace-pre-wrap text-xs opacity-90">{step.error}</pre>
                )}
              </div>
              <span className="shrink-0 rounded-md border border-current/20 px-2 py-0.5 text-xs font-medium capitalize">
                {stateLabel(step.state)}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}
